import { useCallback, useEffect, useState } from 'react';

import {
  DiagnocoinLedgerEntry,
  DiagnocoinLedgerEntryType,
} from '@/shared/api/protocol-ts/api/billing_new/dto_diagnocoin_new_pb';
import { ListDiagnocoinLedgerReq } from '@/shared/api/protocol-ts/api/billing_new/svc_billing_new_pb';
import { useAppDispatch } from '@/shared/hooks';

import { billingModel } from '@/entities/billing';

import {
  DIAGNOCOIN_HISTORY_FILTER_TYPES,
  DIAGNOCOIN_HISTORY_PAGE_SIZE,
  DiagnocoinHistoryFilter,
} from '../config';

type UseDiagnocoinLedgerParams = {
  accountID: string;
  filter: DiagnocoinHistoryFilter;
  isOpen: boolean;
};

type LedgerPage = {
  entries: DiagnocoinLedgerEntry[];
  fetchedCount: number;
  total: number;
  sequenceCeiling: bigint | null;
};

const initialPage: LedgerPage = {
  entries: [],
  fetchedCount: 0,
  total: 0,
  sequenceCeiling: null,
};

export const useDiagnocoinLedger = (params: UseDiagnocoinLedgerParams) => {
  const { accountID, filter, isOpen } = params;

  const dispatch = useAppDispatch();

  const [page, setPage] = useState(initialPage);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const fetchPage = useCallback(
    async (offset: number, ceiling: bigint | null) => {
      setIsLoading(true);

      const types = DIAGNOCOIN_HISTORY_FILTER_TYPES[filter];

      try {
        const response = await dispatch(
          billingModel.thunks.listDiagnocoinLedger(
            new ListDiagnocoinLedgerReq({
              AccountID: accountID,
              Limit: DIAGNOCOIN_HISTORY_PAGE_SIZE,
              Offset: offset,
              ...(ceiling === null ? {} : { SequenceTo: ceiling }),
              ...(types.length === 1 ? { Type: types[0] } : {}),
            }),
          ),
        ).unwrap();

        const fetched = response.Entries ?? [];
        const visible =
          types.length > 1
            ? fetched.filter((entry) =>
                types.includes(entry.Type as DiagnocoinLedgerEntryType),
              )
            : fetched;

        setPage((current) => ({
          entries: offset === 0 ? visible : [...current.entries, ...visible],
          fetchedCount: offset + fetched.length,
          total: Number(response.Total ?? 0),
          sequenceCeiling:
            offset === 0 && fetched.length > 0
              ? fetched[0].Sequence
              : current.sequenceCeiling,
        }));
        setHasFailed(false);
      } catch {
        setHasFailed(true);
      } finally {
        setIsLoading(false);
      }
    },
    [accountID, filter],
  );

  useEffect(() => {
    if (!isOpen || !accountID) {
      return;
    }

    setPage(initialPage);
    fetchPage(0, null);
  }, [isOpen, accountID, filter, fetchPage]);

  const loadMore = () => {
    if (isLoading) {
      return;
    }

    fetchPage(page.fetchedCount, page.sequenceCeiling);
  };

  return {
    entries: page.entries,
    isLoading,
    hasFailed,
    hasMore: page.fetchedCount > 0 && page.fetchedCount < page.total,
    loadMore,
  };
};
